import { UserTypeEnum } from '@/enums/UserTypeEnum';
import { FacilitySettingsModel } from '@/models/FacilitySettings';
import { normalizeIp, validateIpv4ForWhitelist } from '@/utils/ipWhitelist';
import firebase from 'firebase';
import { AuditLogsTier1 as Tier1, AuditLogsTier2 as Tier2, AuditLogsTier3 as Tier3 } from '@/enums/AuditLogs';

export default {
  name: "Security",
  data() {
    return {
      loading: false,
      newIp: '',
      ipInputError: '',
      allowedIps: [] as string[],
      onIpsUnsubscribe: null as null | Function,
    }
  },
  computed: {
    facility(): FacilitySettingsModel {
      return this.$store.state.facilityModule.selectedFacility;
    },
    staffUser() {
      return this.$store.state.authModule.user;
    },
    canAddIp(): boolean {
      const res = validateIpv4ForWhitelist(this.newIp, this.allowedIps);
      return res.ok === true;
    }
  },
  async created() {
    this.subscribeOnIpWhitelist();
  },
  beforeDestroy() {
    if (this.onIpsUnsubscribe) {
      this.onIpsUnsubscribe();
      this.onIpsUnsubscribe = null;
    }
  },
  methods: {
    subscribeOnIpWhitelist() {
      if (this.onIpsUnsubscribe || !this.facility?.orgId) return;

      this.loading = true;

      const ref = firebase.firestore().collection('organisations').doc(this.facility.orgId)
        .collection('ipwhitelist');

      this.onIpsUnsubscribe = ref.onSnapshot((snap) => {
        const ips: string[] = [];

        snap.forEach((doc) => {
          const ip = normalizeIp((doc.data() as any)?.ip);
          if (ip) ips.push(ip);
        });

        this.allowedIps = Array.from(new Set(ips)).sort();
        this.loading = false;

        if (this.newIp && this.loading === false) {
          this.onIpInputChanged();
        }

      }, (error) => {
        console.error('subscribeOnIpWhitelist() error:', error);
        this.loading = false;
      });
    },
    onIpInputChanged() {
      const ip = normalizeIp(this.newIp);

      if (!ip) {
        this.ipInputError = '';
        return;
      }

      const res = validateIpv4ForWhitelist(ip, this.allowedIps);
      this.ipInputError = res.ok ? '' : res.error;
    },
    async addIp() {
      const res = validateIpv4ForWhitelist(this.newIp, this.allowedIps);

      if (!res.ok) {
        this.ipInputError = res.error;
        return;
      }

      this.loading = true;
      try {
        const ipToAdd = res.ip;

        this.newIp = '';
        this.ipInputError = '';

        await firebase.firestore().collection('organisations').doc(this.facility.orgId)
        .collection('ipwhitelist').add({
           ip: ipToAdd,
           createdById: this.staffUser.id,
           createdByType: UserTypeEnum.Staff,
           typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.Security}`]
        })
      } finally {
        this.loading = false;
      }
    },
    async removeIp(ip: string) {
      this.loading = true;
      try {
        const normalized = normalizeIp(ip);

        const snap = await firebase.firestore().collection('organisations').doc(this.facility.orgId)
          .collection('ipwhitelist').where('ip', '==', normalized).get()

        const batch = firebase.firestore().batch();
        snap.forEach((doc) => batch.delete(doc.ref));
        if (!snap.empty) {
          await batch.commit();
           this.$toast.open({
            type: 'success',
            message: `IP ${ip} was successfully removed from the whitelist`,
            duration: 4000,
            dismissible: true,
          });
        }

        if (this.newIp) {
          this.onIpInputChanged();
        }
      } finally {
        this.loading = false;
      }
    }
  }
}